import { Http, HttpApi, HttpEndpoint, NetworkClient, s, WebSocket } from "@kosmod/pancake";
import { LogInInput, SignUpInput } from "../defs/signup";
import { AccountDescription, GuestPassDescription, loginAPI, LoginClient } from "../defs";
import { useEffect, useMemo, useState } from "@lukekaalim/act";
import { LocalStorageService, useLocalStorage } from "@kosmod/core";
import { useAsync, useLogger } from "@kosmod/kit";

import { filter, firstValueFrom, map, Subject, tap } from 'rxjs';
import { ConfirmCredentialMessage, SendCredentialMessage, SESAME_AUTH_SUBPROTOCOL } from "../defs/socket";

export type AuthState = {
  authority: 'unknown' | 'none' | 'guest' | 'user',
  account: null | AccountDescription,
  guestpass: null | GuestPassDescription,

  login(input: LogInInput): Promise<void>,
  signup(input: SignUpInput): Promise<void>,
  logout(): void,

  insertGuestCode(code: string): Promise<void>,


  net: NetworkClient,
  loginClient: LoginClient,
};

export const CREDENTIAL_STORAGE_KEY = `kosmod/credential`
const WS_AUTH = {
  NOT_READY: 'not-ready',
  NO_AUTH_NEEDED: 'no-auth',
  WAITING_FOR_AUTH_ACCEPT: 'waiting',
  AUTH_REJECTED: 'rejected',
  AUTH_ACCEPTED: 'accepted',
} as const;
type WSAuthClientState = typeof WS_AUTH[keyof typeof WS_AUTH];

/**
 * 
 * @param httpClient 
 * @param loginClient 
 * @param credentialStorage 
 * @param authorizationHosts A set of hosts that will cause the
 *  authorizedHTTPClient to provide the requested url with an
 *  Authorization header containing the token.
 * @returns 
 */
export const useAuthStateProvider = (
  net: NetworkClient,
  loginURLs: HttpApi.URLMap<typeof loginAPI>,

  credentialStorage: LocalStorageService,

  authorizationHosts: Set<string>
): AuthState => {
  const [credential, setCredential] = useLocalStorage(CREDENTIAL_STORAGE_KEY, s.optional(s.str), null, credentialStorage);
  const logger = useLogger('Auth')

  const authorizedHTTPClient = useMemo((): Http.Client => {
    return {
      request(url, options) {
        let headers = options.headers || {};
        if (credential && authorizationHosts.has(url.hostname)) {
          headers['authorization'] = `Bearer ${credential}`
        }
        return net.http.request(url, { ...options, headers })
      },
    }
  }, [net.http, authorizationHosts, credentialStorage, credential])

  // TODO: How can an arbitrary Websocket client

  const authorizedWSClient = useMemo((): WebSocket.Client => {
    return {
      connect(url, protocols = []) {
        const inner = net.ws.connect(url, protocols);
        const receive = new Subject<string | ArrayBuffer>();

        let authState: WSAuthClientState = WS_AUTH.NOT_READY;

        const ready = inner.ready.then(async ({ protocol }) => {
          const multiplexedProtocols = protocol.split('+')
          if (!multiplexedProtocols.includes(SESAME_AUTH_SUBPROTOCOL)) {
            authState = WS_AUTH.NO_AUTH_NEEDED;
            return { protocol }
          };
          
          const protocolsWithoutAuth = multiplexedProtocols
            .filter(p => p !== SESAME_AUTH_SUBPROTOCOL)
            .join('+');

          if (!credential) {
            authState = WS_AUTH.AUTH_REJECTED;
            inner.disconnect(1008);
            return { protocol: protocolsWithoutAuth }
          }
          
          inner.port.send(JSON.stringify({
            type: 'authorize',
            token: credential
          } as SendCredentialMessage))

          authState = WS_AUTH.WAITING_FOR_AUTH_ACCEPT;

          const sub = inner.port.receive.subscribe(message => {
            // logger.log(message)
            if (typeof message !== 'string')
              return null;
            const json = JSON.parse(message);
            if (json['type'] === 'authorize-confirm') {
              sub.unsubscribe();
              const confirmation = json as ConfirmCredentialMessage;

              if (confirmation.result === 'accept') {
                authState = WS_AUTH.AUTH_ACCEPTED;
                // Happy path!
                inner.port.receive.subscribe(receive);
              } else {
                authState = WS_AUTH.AUTH_REJECTED;
                inner.disconnect(1008);
              }
            }
          })

          return { protocol: protocolsWithoutAuth };
        })

        return {
          disconnect: inner.disconnect,
          disconnected: inner.disconnected,
          ready,
          port: {
            send(message) {
              switch (authState) {
                case WS_AUTH.NOT_READY:
                case WS_AUTH.WAITING_FOR_AUTH_ACCEPT:
                case WS_AUTH.AUTH_REJECTED:
                  return;
                case WS_AUTH.NO_AUTH_NEEDED:
                case WS_AUTH.AUTH_ACCEPTED:
                  return inner.port.send(message);
              }
            },
            receive: receive.pipe()
          },
        }
      },
    }
  }, [net.http, authorizationHosts, credentialStorage, credential])

  const authorizedNet = useMemo((): NetworkClient => {
    return {
      http: authorizedHTTPClient,
      ws: authorizedWSClient,
      rtc: net.rtc,
    }
  }, [authorizedHTTPClient, authorizedWSClient])

  const loginClient = useMemo(() => HttpApi.createClientFromURLMap(
    loginAPI,
    loginURLs,
    authorizedNet
  ), [loginURLs, net, authorizedHTTPClient])

  const { result: accountResult, state: accountResultState } = useAsync(async () => {
    if (!credential)
      return null;

    return await loginClient.accounts.GET(null, null, {})
  }, [loginClient, credential, authorizedHTTPClient])

  const guestpass = null;


  const authority = useMemo(() => {
    if (!credential)
      return 'none';
    
    if (accountResultState === 'init' || accountResultState === 'restarted')
      return 'unknown';

    if (accountResult && accountResult.account)
      return 'user';

    if (guestpass)
      return 'guest';

    return 'none';
  }, [accountResult, guestpass, accountResultState])

  return useMemo(() => ({
    net: authorizedNet,
    loginClient,

    account: accountResult && accountResult.account || null,
    guestpass,

    authority,

    async login(input) {
      const result = await loginClient.tokens.login.POST({}, input);
      if (result.op === 'err')
        throw new Error(result.message);

      setCredential(result.token);
    },
    async signup(input) {
      const { token } = await loginClient.accounts.POST({}, input);
      setCredential(token);
    },
    logout() {
      if (!accountResult)
        throw new Error(`Cannot log out - not logged in!`);
      
      loginClient.tokens["/"].DELETE({}, { tokenId: accountResult.tokenId })
      setCredential(null);
    },
    async insertGuestCode(code) {
      throw new Error(`Guests not yet supported`)
    },
  }), [accountResult, authorizedNet, guestpass, authority, loginClient])
};
